import { AppButtonService } from '@office-start/app-button-service';
import { logCopilotFunnelEvent, logTelemetryOnPageVisibilityChange, logTelemetryOnUnload, } from '@office-start/copilot-fre-telemetry-service';
import { mark as markPerformance } from '@office-start/performance-service';
import { getWindowsProcessStartTime } from '@office-start/performance-utility';
import { ReplaySubject } from 'rxjs';
import { getResourceDeliveryType } from './utilities/getResourceDeliveryType';
export class AppbarConnectorService {
    constructor() {
        var _a;
        this._isTileChange = false;
        this.appButtonService = new AppButtonService();
        this.currentAppIconPositionSubject = new ReplaySubject(1);
        // For desktop, we will use the timeline wrt process start time
        this._userClickTime = (_a = getWindowsProcessStartTime()) !== null && _a !== void 0 ? _a : Date.now();
        this._appUrl = window.location.pathname;
        this._pauseSchedulerForMosApp = false;
        this._pauseSchedulerInAHCInternal = false;
        this._resourceDeliveryType = getResourceDeliveryType();
        this._stageBEndTime;
        this.setTimeElapsedSinceDocumentRender();
        logCopilotFunnelEvent('UserLandedonHarmony', {
            ElapsedTime: '0',
        });
        // Log telemetry when the user closes the browser or the tab
        window.addEventListener('beforeunload', logTelemetryOnUnload);
        window.addEventListener('visibilitychange', logTelemetryOnPageVisibilityChange);
    }
    static getInstance() {
        if (!AppbarConnectorService.instance) {
            AppbarConnectorService.instance = new AppbarConnectorService();
            markPerformance("Appbar_Connector_Service_Start" /* KnownPerformanceMarks.AppbarConnectorServiceStart */, 0 /* PerformanceMeasureType.NoMeasure */);
        }
        return AppbarConnectorService.instance;
    }
    /**
     * get time user clicked on the app tile
     */
    get userClickTime() {
        return this._userClickTime;
    }
    /**
     * get whether tile change happened or not
     */
    get tileChanged() {
        return this._isTileChange;
    }
    /**
     * get appUrl
     */
    get appUrl() {
        return this._appUrl;
    }
    /**
     * get pauseSchedulerForMosApp
     */
    get pauseSchedulerForMosApp() {
        return this._pauseSchedulerForMosApp;
    }
    /**
     * get pauseSchedulerInAHCInternal
     */
    get pauseSchedulerInAHCInternal() {
        return this._pauseSchedulerInAHCInternal;
    }
    /**
     * get timeelapsedSinceDocumentRender
     */
    get timeElapsedSinceDocuementRender() {
        return this._timeElapsedSinceDocumentRender;
    }
    /**
     * get _stageBEndTime
     */
    get stageBEndTime() {
        return this._stageBEndTime;
    }
    get resourceDeliveryType() {
        return this._resourceDeliveryType;
    }
    /**
     * move focus to currently active app's tile on app bar
     */
    focusOnAppbar() {
        if (!this.onReturnFocus) {
            return;
        }
        this.onReturnFocus();
    }
    /**
     * register function for setting focus to currently active app's tile on app bar
     */
    setOnReturnFocus(callback) {
        this.onReturnFocus = callback;
    }
    /**
     * set function for setting pauseSchedulerForMosApp
     */
    setPauseSchedulerForMosApp(pauseSchedulerForMosApp) {
        this._pauseSchedulerForMosApp = pauseSchedulerForMosApp;
    }
    /**
     * set function for setting pauseSchedulerInAHCInternal
     */
    setPauseSchedulerInAHCInternal(pauseSchedulerInAHCInternal) {
        this._pauseSchedulerInAHCInternal = pauseSchedulerInAHCInternal;
    }
    /**
     * set callback for getting appIcon's position from appbar
     * @param callback
     */
    setGetAppIconPositionRect(callback) {
        this.getAppIconPositionRect = callback;
    }
    /**
     * set callback for getting appIcon's element ref from appbar
     * @param callback
     */
    setGetAppIconElementRef(callback) {
        this.getAppIconElementRef = callback;
    }
    /**
     * set function for setting _stageBEndTime
     */
    setStageBEndTime(stageBEndTime) {
        this._stageBEndTime = stageBEndTime;
    }
    setInteractiveFunctions(instance) {
        this.setOnReturnFocus(() => instance === null || instance === void 0 ? void 0 : instance.focus());
        this.setGetAppIconPositionRect((id) => {
            return instance === null || instance === void 0 ? void 0 : instance.getAppIconPositionRect(id);
        });
        this.setGetAppIconElementRef((id) => {
            return instance === null || instance === void 0 ? void 0 : instance.getAppIconElementRef(id);
        });
    }
    /**
     * Callback for app button click
     */
    onAppButtonClick(clickTime, isAppRunning) {
        this._userClickTime = clickTime;
        this._isTileChange = true;
        this._appUrl = window.location.pathname;
        if (isAppRunning) {
            //fire click event for app to trigger callback on currently running app tile click in app bar
            this.appButtonService.dispatchEvent('click', {});
        }
    }
    /**
     * fire hoverEnter event for app to trigger callback on currently running app tile hover enter in app bar
     */
    onAppButtonHoverEnter() {
        this.appButtonService.dispatchEvent('hoverEnter', {});
    }
    /**
     * fire hoverLeave event for app to trigger callback on currently running app tile hover leave in app bar
     */
    onAppButtonHoverLeave() {
        this.appButtonService.dispatchEvent('hoverLeave', {});
    }
    /**
     * return AppButtonService instance
     */
    getAppButtonService() {
        return this.appButtonService;
    }
    /**
     * Update currently running app's icon's vertical position information
     */
    updateCurrentAppVerticalPosition() {
        var _a, _b;
        const val = (_b = (_a = this.getAppIconPositionRect) === null || _a === void 0 ? void 0 : _a.call(this)) === null || _b === void 0 ? void 0 : _b.top;
        if (val) {
            this.currentAppIconPositionSubject.next(val);
        }
    }
    /**
     * Return observable for getting latest vertical position value for currently running app's icon
     * @returns
     */
    getCurrentAppIconPositionObservable() {
        return this.currentAppIconPositionSubject.asObservable();
    }
    /**
     * Set time from Response End to App Bar Connector Start Time
     */
    setTimeElapsedSinceDocumentRender() {
        var _a;
        try {
            // Try to measure the performance between two marks
            performance.measure('Copilot_BundleLoad_Time', 'Copilot_Execution_Starts');
            const measure = performance.getEntriesByName('Copilot_BundleLoad_Time')[0];
            this._timeElapsedSinceDocumentRender =
                (_a = Math.round(measure.duration)) !== null && _a !== void 0 ? _a : 0;
        }
        catch (error) {
            this._timeElapsedSinceDocumentRender = 0;
        }
    }
    getIsCopilotChatExperience() {
        if (window) {
            return window.location.pathname.includes('/chat');
        }
        return false;
    }
    getIsCopilotChatExperienceWithDirectMcm() {
        if (window) {
            return (window.location.pathname.includes('/chat') &&
                !this._isTileChange);
        }
        return false;
    }
}
//# sourceMappingURL=AppbarConnectorService.js.map